import React from 'react'

import { nutrimaticLabels } from '@/nutrimatic'
import { getActiveChainId } from '@/config/active'
import type { SwapControllerResult } from '../../../../features/swap/useSwapController'
import { Field } from '../../../../domain/trading'
import {
  getDirectedSpotQuote,
  type OnChainPairData,
} from '../../../../features/market/useOnChainPairData'
import { useIndexedPoolForCurrencies } from '../../../../features/market/useIndexedPoolForCurrencies'
import { resolveMarketPairLegs } from '../../../../features/market/resolveMarketPairLegs'
import { PriceImpactReadout } from '../../PriceImpactReadout'

/** Telemetría de cotización integrada al pie del trade stack (mismo card que From/To). */
export function MobileSwapQuotePreview({ controller }: { controller: SwapControllerResult }) {
  const { priceImpactDisplay, priceImpactLimitIsPairSpecific, trade } = controller

  if (!trade) {
    return (
      <div className="nm-mobile-simple__trade-telemetry">
        <p className="nm-mobile-simple__trade-telemetry-idle">
          {nutrimaticLabels.mobileSimple.quoteIdle}
        </p>
      </div>
    )
  }

  return (
    <div className="nm-mobile-simple__trade-telemetry">
      <p className="nm-mobile-simple__trade-telemetry-receive">
        {nutrimaticLabels.mobileSimple.approxReceive(
          trade.outputAmount.toSignificant(6),
          trade.outputAmount.currency.symbol ?? '',
        )}
      </p>
      <div className="nm-mobile-simple__trade-telemetry-readout">
        <PriceImpactReadout
          display={priceImpactDisplay}
          limitIsPairSpecific={priceImpactLimitIsPairSpecific}
          variant="telemetry"
        />
      </div>
    </div>
  )
}

/** Rate hero: market base/quote — independent of trade direction invert. */
export function MobileSwapRateHero({
  controller,
  pairData,
}: {
  controller: SwapControllerResult
  pairData: OnChainPairData
}) {
  const chainId = getActiveChainId()
  const pay = controller.currencies[Field.INPUT]
  const receive = controller.currencies[Field.OUTPUT]
  const indexedPool = useIndexedPoolForCurrencies(pay, receive)
  const { base, quote, pairLabel } = resolveMarketPairLegs(pay, receive, indexedPool)
  const [showInverted, setShowInverted] = React.useState(false)

  // Reset invert only when the market pair identity changes — not when trade flips.
  React.useEffect(() => {
    setShowInverted(false)
  }, [pairLabel, indexedPool?.pairAddress])

  const spotPay = showInverted ? quote : base
  const spotReceive = showInverted ? base : quote

  const spotQuote = React.useMemo(
    () => getDirectedSpotQuote(pairData, spotPay, spotReceive, chainId),
    [chainId, pairData, spotPay, spotReceive],
  )

  if (!base || !quote) {
    return (
      <p className="nm-mobile-simple__rate-hero nm-mobile-simple__rate-hero--idle">
        {nutrimaticLabels.mobileSimple.rateIdle}
      </p>
    )
  }

  if (pairData.loading) {
    return (
      <p className="nm-mobile-simple__rate-hero nm-mobile-simple__rate-hero--idle">
        {nutrimaticLabels.mobileSimple.rateIdle}
      </p>
    )
  }

  if (!pairData.exists || !spotQuote) {
    return (
      <p className="nm-mobile-simple__rate-hero nm-mobile-simple__rate-hero--idle">
        Sin pool on-chain para este par
      </p>
    )
  }

  return (
    <button
      type="button"
      className="nm-mobile-simple__rate-hero"
      onClick={() => setShowInverted((value) => !value)}
      title={nutrimaticLabels.mobileSimple.invertRate}
    >
      1 {spotQuote.paySymbol} = {spotQuote.rateAmount} {spotQuote.rateUnitSymbol}
      <span className="nm-mobile-simple__rate-hero-hint" aria-hidden>
        ↻
      </span>
    </button>
  )
}
